import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  CpuChipIcon, 
  CogIcon, 
  ChartBarIcon, 
  BoltIcon 
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

const HeroMatrix: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  // Parallax transforms
  const y1 = useTransform(scrollY, [0, 300], [0, -50]);
  const y2 = useTransform(scrollY, [0, 300], [0, -25]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0.8]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const serviceHighlights = [
    {
      icon: CpuChipIcon,
      title: t('services.highlights.ai.title'),
      description: t('services.highlights.ai.description'),
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: CogIcon,
      title: t('services.highlights.automation.title'),
      description: t('services.highlights.automation.description'),
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: ChartBarIcon,
      title: t('services.highlights.dataPipelines.title'),
      description: t('services.highlights.dataPipelines.description'),
      color: 'from-green-500 to-emerald-500',
    },
    {
      icon: BoltIcon,
      title: t('services.highlights.noCode.title'),
      description: t('services.highlights.noCode.description'),
      color: 'from-orange-500 to-red-500',
    },
  ];

  return (
    <div ref={ref} className="relative overflow-hidden">
      {/* Background Matrix Effect */}
      <div className="absolute inset-0 opacity-10">
        <div 
          className="absolute inset-0 bg-gradient-to-br from-primary-500/20 to-secondary-500/20"
          style={{
            transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)`,
          }}
        />
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-primary-400 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                opacity: [0.2, 0.8, 0.2],
                scale: [1, 1.5, 1],
              }}
              transition={
                {
                  duration: 2 + Math.random() * 3,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }
              }
            />
          ))}
        </div>
      </div>

      <motion.div 
        className="relative z-10 bg-gray-800 border border-gray-700 rounded-2xl p-8 lg:p-12"
        style={{ y: y1, opacity }}
      >
        {/* Main Hero Content */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-6"
          >
            <h1 className="text-4xl lg:text-6xl font-bold mb-4">
              <span className="bg-gradient-to-r from-primary-400 via-secondary-400 to-primary-400 bg-clip-text text-transparent">
                {t('hero.name')}
              </span>
            </h1>
            <p className="text-xl lg:text-2xl text-gray-300 mb-6">
              {t('hero.title')}
            </p>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-lg text-gray-300 max-w-3xl mx-auto mb-8 leading-relaxed"
          >
            {t('hero.tagline')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button 
              className="btn-primary text-lg px-8 py-4"
              onClick={() => navigate(`/${i18n.language || 'en'}/consultation`)}
            >
              {t('hero.cta')}
            </button>
            <button 
              className="btn-secondary text-lg px-8 py-4"
              onClick={() => navigate(`/${i18n.language || 'en'}/portfolio`)}
            >
              {t('hero.seeWork')}
            </button>
          </motion.div>
        </div>

        {/* Service Highlights Grid */}
        <motion.div 
          style={{ y: y2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {serviceHighlights.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                whileHover={{ 
                  scale: 1.05,
                  transition: { duration: 0.2 }
                }}
                className="relative group"
              >
                <div className="bg-gray-700/50 backdrop-blur-sm border border-gray-600 rounded-xl p-6 h-full transition-all duration-300 group-hover:border-gray-500">
                  {/* Icon with gradient background */}
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${service.color} p-3 mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-full h-full text-white" />
                  </div>
                  
                  <h3 className="text-white font-semibold mb-2 group-hover:text-primary-300 transition-colors">
                    {service.title}
                  </h3>
                  
                  <p className="text-gray-400 text-sm">
                    {service.description}
                  </p>
                  
                  {/* Hover effect overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 rounded-xl transition-opacity duration-300`} />
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Floating Elements */}
        <div className="absolute top-10 right-10 opacity-20">
          <motion.div
            animate={{
              rotate: 360,
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear",
            }}
            className="w-20 h-20 border border-primary-400 rounded-full"
          />
        </div>
        
        <div className="absolute bottom-10 left-10 opacity-20">
          <motion.div
            animate={{
              rotate: -360,
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "linear",
            }}
            className="w-16 h-16 border border-secondary-400 rounded-lg"
          />
        </div>
      </motion.div>
    </div>
  );
};

export default HeroMatrix;